/**
 * Sloka sound display — Sanskrit + phonetic transliteration only.
 * No meaning/translation in other languages (mantra sound stays identical).
 */

const TRANSLIT_LANGS = ["en", "hi", "te", "ta", "kn", "ml", "gu", "bn", "or", "pa", "sa"];

function hydrateTransliterationLineBreaks(text, sanskrit) {
  if (!text || typeof text !== "string") return text;
  const normalized = text.replace(/\r\n/g, "\n");
  if (normalized.includes("\n")) return normalized;
  if (!(typeof sanskrit === "string" && sanskrit.includes("\n"))) return normalized;

  // Legacy/admin content sometimes stores transliteration as one paragraph.
  // Recreate readable chant line breaks from mantra delimiters.
  const withVerseBreaks = normalized
    .replace(/\s*॥\s*(\d+\s*॥)?\s*/g, (_m, numbered) => (numbered ? ` ॥ ${String(numbered).trim()}\n` : " ॥\n"))
    .replace(/\s*।\s*/g, " ।\n")
    .replace(/\n{2,}/g, "\n")
    .trim();

  return withVerseBreaks || normalized;
}

/**
 * Normalize a slide into { sanskrit, transliteration: { [lang]: string } }.
 * Migrates legacy fields: transliteration string, sanskrit_iast, transliteration_xx.
 */
export function normalizeSlokaSlide(slide) {
  if (!slide || typeof slide !== "object") {
    return { timestamp: 0, sanskrit: "", transliteration: {} };
  }

  const transliteration = {};
  if (slide.transliteration && typeof slide.transliteration === "object" && !Array.isArray(slide.transliteration)) {
    for (const [k, v] of Object.entries(slide.transliteration)) {
      if (typeof v === "string" && v.trim()) transliteration[k] = v;
    }
  }

  for (const lang of TRANSLIT_LANGS) {
    const key = `transliteration_${lang}`;
    if (typeof slide[key] === "string" && slide[key].trim()) {
      transliteration[lang] = slide[key];
    }
  }

  // Legacy single transliteration / IAST → English phonetic line
  const legacy =
    (typeof slide.transliteration === "string" && slide.transliteration.trim() ? slide.transliteration : "") ||
    (typeof slide.sanskrit_iast === "string" && slide.sanskrit_iast.trim() ? slide.sanskrit_iast : "") ||
    "";
  if (legacy && !transliteration.en) transliteration.en = legacy;

  return {
    timestamp: Number(slide.timestamp) || 0,
    sanskrit: String(slide.sanskrit || slide.sanskrit_devanagari || ""),
    transliteration,
  };
}

/**
 * Resolve display transliteration for user's language.
 * Same mantra sound — different script/phonetic convention.
 */
export function resolveSlokaTransliteration(slide, lang = "en") {
  const n = normalizeSlokaSlide(slide);
  const code = String(lang || "en").toLowerCase().slice(0, 2);
  const rawText =
    n.transliteration[code] ||
    n.transliteration.en ||
    n.transliteration.sa ||
    Object.values(n.transliteration)[0] ||
    "";
  const text = hydrateTransliterationLineBreaks(rawText, n.sanskrit);
  return {
    sanskrit: n.sanskrit,
    transliteration: text || null,
    transliteration_language: text
      ? n.transliteration[code]
        ? code
        : n.transliteration.en
          ? "en"
          : Object.keys(n.transliteration)[0] || code
      : null,
    timestamp: n.timestamp,
  };
}

/** API shape: { sanskrit, transliteration: { en, te, ... } } */
export function slokaApiPayload(slide, lang) {
  const n = normalizeSlokaSlide(slide);
  const resolved = resolveSlokaTransliteration(slide, lang);
  return {
    timestamp: n.timestamp,
    sanskrit: n.sanskrit,
    transliteration: n.transliteration,
    transliteration_language: resolved.transliteration_language,
    transliteration_text: resolved.transliteration,
  };
}

export function prepareSlidesForSave(slides) {
  return (slides || [])
    .map((s) => {
      const n = normalizeSlokaSlide(s);
      // Also accept admin flat fields transliteration_en etc. already folded in normalize
      if (!n.sanskrit) return null;
      return {
        timestamp: n.timestamp,
        sanskrit: n.sanskrit,
        transliteration: n.transliteration,
        // Keep IAST alias for older clients
        sanskrit_iast: n.transliteration.en || undefined,
      };
    })
    .filter(Boolean);
}

export { TRANSLIT_LANGS };
